import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';

export type ColumnDataType = 'text' | 'date' | 'select' | 'number' | 'color-select';
export type TextFilterMode = 'search' | 'equals' | 'regex';
export type NumberFilterMode = 'equals' | 'gt' | 'gte' | 'lt' | 'lte' | 'between';

export interface ColorSelectOption {
  label: string;
  color: string;
}

export interface ColumnFilterDialogData {
  columnName: string;          // Display header, e.g. "Inception Date"
  field: string;               // Field key, e.g. "inceptionDate"
  dataType: ColumnDataType;    // 'text' | 'date' | 'select' | 'color-select'
  selectOptions?: string[];    // Dropdown options for 'select' type
  colorSelectOptions?: ColorSelectOption[]; // Options with color dots for 'color-select' type
  currentSort?: 'A' | 'D' | '';
  currentFilter?: ColumnFilterValue;
  showSort?: boolean;          // Whether to show the Sort section (default: true)
}

export interface ColumnFilterValue {
  textValue?: string;             // For text search
  textFilterMode?: TextFilterMode; // Filter mode: search (contains), equals, regex
  selectValues?: string[];        // For checkbox multi-selection
  datePreset?: 'today' | 'last7' | 'custom' | '';
  dateFrom?: Date | null;         // For date range
  dateTo?: Date | null;           // For date range
  numberFilterMode?: NumberFilterMode; // For number filtering
  numberValue?: number | null;        // Single value for equals/gt/gte/lt/lte
  numberMin?: number | null;          // Min value for between
  numberMax?: number | null;          // Max value for between
}

export interface ColumnFilterDialogResult {
  sort?: 'A' | 'D' | '';
  filter?: ColumnFilterValue;
  cleared?: boolean;
}

@Component({
  selector: 'app-column-filter-dialog',
  templateUrl: './column-filter-dialog.component.html',
  styleUrls: ['./column-filter-dialog.component.scss'],
  standalone: false,
  encapsulation: ViewEncapsulation.None
})
export class ColumnFilterDialogComponent {
  columnName: string;
  field: string;
  dataType: ColumnDataType;
  selectOptions: string[] = [];
  colorSelectOptions: ColorSelectOption[] = [];
  showSort = true;

  sortDirection: 'A' | 'D' | '' = '';
  filterPreset: 'today' | 'last7' | 'custom' | '' = '';
  textFilterMode: TextFilterMode = 'search';
  textFilter = new FormControl('');
  optionSearchFilter = new FormControl('');
  selectedItems: Set<string> = new Set();
  selectAll = true;
  dateFrom = new FormControl<Date | null>({value: null, disabled: true});
  dateTo = new FormControl<Date | null>({value: null, disabled: true});
  numberFilterMode: NumberFilterMode = 'equals';
  numberValue = new FormControl<number | null>(null);
  numberMin = new FormControl<number | null>(null);
  numberMax = new FormControl<number | null>(null);

  constructor(
    public dialogRef: MatDialogRef<ColumnFilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ColumnFilterDialogData
  ) {
    this.columnName = data.columnName;
    this.field = data.field;
    this.showSort = data.showSort !== false;
    this.dataType = data.dataType;
    this.selectOptions = data.selectOptions || [];
    this.colorSelectOptions = data.colorSelectOptions || [];
    if (this.dataType === 'color-select' && !this.selectOptions.length) {
      this.selectOptions = this.colorSelectOptions.map(o => o.label);
    }
    this.sortDirection = data.currentSort || '';

    // Initialise checkbox selections
    if (data.currentFilter && data.currentFilter.selectValues && data.currentFilter.selectValues.length > 0) {
      this.selectedItems = new Set(data.currentFilter.selectValues);
      this.selectAll = this.selectedItems.size === this.selectOptions.length;
    } else if (this.dataType === 'select' || this.dataType === 'color-select') {
      // Default: all selected
      this.selectedItems = new Set(this.selectOptions);
      this.selectAll = true;
    }

    if (data.currentFilter) {
      this.textFilterMode = data.currentFilter.textFilterMode || 'search';
      this.textFilter.setValue(data.currentFilter.textValue || '');
      this.filterPreset = data.currentFilter.datePreset || '';
      if (this.filterPreset) {
        this.dateFrom.enable();
        this.dateTo.enable();
      }
      this.dateFrom.setValue(data.currentFilter.dateFrom || null);
      this.dateTo.setValue(data.currentFilter.dateTo || null);
      this.numberFilterMode = data.currentFilter.numberFilterMode || 'equals';
      this.numberValue.setValue(data.currentFilter.numberValue ?? null);
      this.numberMin.setValue(data.currentFilter.numberMin ?? null);
      this.numberMax.setValue(data.currentFilter.numberMax ?? null);
    }

    // Auto-select matching items when user types in the search field
    this.optionSearchFilter.valueChanges.subscribe(val => {
      const search = (val || '').trim().toLowerCase();
      if (!search) {
        // Empty search: restore all selected
        this.selectedItems = new Set(this.selectOptions);
        this.selectAll = true;
      } else {
        const terms = search.split(',').map(t => t.trim()).filter(t => !!t);
        this.selectedItems.clear();
        this.selectOptions.forEach(opt => {
          const lower = opt.toLowerCase();
          if (terms.some(term => lower.includes(term))) {
            this.selectedItems.add(opt);
          }
        });
        this.selectAll = this.selectedItems.size === this.selectOptions.length;
      }
    });
  }

  sortAscending(): void {
    this.sortDirection = 'A';
    this.dialogRef.close({ sort: 'A' } as ColumnFilterDialogResult);
  }

  sortDescending(): void {
    this.sortDirection = 'D';
    this.dialogRef.close({ sort: 'D' } as ColumnFilterDialogResult);
  }

  onPresetChange(preset: 'today' | 'last7' | 'custom'): void {
    this.filterPreset = preset;
    if (preset === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      this.dateFrom.enable();
      this.dateTo.enable();
      this.dateFrom.setValue(today);
      this.dateTo.setValue(today);
    } else if (preset === 'last7') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const last7 = new Date(today);
      last7.setDate(last7.getDate() - 7);
      this.dateFrom.enable();
      this.dateTo.enable();
      this.dateFrom.setValue(last7);
      this.dateTo.setValue(today);
    } else {
      this.dateFrom.enable();
      this.dateTo.enable();
      this.dateFrom.setValue(null);
      this.dateTo.setValue(null);
    }
  }

  toggleSelectAll(): void {
    if (this.selectAll) {
      this.selectedItems = new Set(this.selectOptions);
    } else {
      this.selectedItems.clear();
    }
  }

  toggleItem(item: string): void {
    if (this.selectedItems.has(item)) {
      this.selectedItems.delete(item);
    } else {
      this.selectedItems.add(item);
    }
    this.selectAll = this.selectedItems.size === this.selectOptions.length;
  }

  clearFilter(): void {
    this.sortDirection = '';
    this.filterPreset = '';
    this.textFilterMode = 'search';
    this.textFilter.setValue('');
    this.optionSearchFilter.setValue('');
    this.selectedItems = new Set(this.selectOptions);
    this.selectAll = true;
    this.dateFrom.setValue(null);
    this.dateTo.setValue(null);
    this.dateFrom.disable();
    this.dateTo.disable();
    this.numberFilterMode = 'equals';
    this.numberValue.setValue(null);
    this.numberMin.setValue(null);
    this.numberMax.setValue(null);
    this.dialogRef.close({ cleared: true } as ColumnFilterDialogResult);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  getColorForOption(label: string): string {
    const opt = this.colorSelectOptions.find(o => o.label === label);
    return opt ? opt.color : 'transparent';
  }

  get filteredSelectOptions(): string[] {
    const search = (this.optionSearchFilter.value || '').trim().toLowerCase();
    if (!search) return this.selectOptions;
    const terms = search.split(',').map(t => t.trim()).filter(t => !!t);
    if (!terms.length) return this.selectOptions;
    return this.selectOptions.filter(opt => {
      const lower = opt.toLowerCase();
      return terms.some(term => lower.includes(term));
    });
  }

  apply(): void {
    const result: ColumnFilterDialogResult = {};

    if (this.dataType === 'text') {
      result.filter = { textValue: (this.textFilter.value || '').trim(), textFilterMode: this.textFilterMode };
    } else if (this.dataType === 'select' || this.dataType === 'color-select') {
      // Only store filter when not all items are selected
      const selected = Array.from(this.selectedItems);
      result.filter = { selectValues: selected.length < this.selectOptions.length ? selected : [] };
    } else if (this.dataType === 'date') {
      result.filter = {
        datePreset: this.filterPreset,
        dateFrom: this.dateFrom.value,
        dateTo: this.dateTo.value
      };
    } else if (this.dataType === 'number') {
      result.filter = {
        numberFilterMode: this.numberFilterMode,
        numberValue: this.numberValue.value,
        numberMin: this.numberMin.value,
        numberMax: this.numberMax.value
      };
    }

    this.dialogRef.close(result);
  }
}
